import React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import AudioRecorder from '../../utilities/AudioRecorder';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  PlusIcon,
  EllipsisVerticalIcon,
  XMarkIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ClockIcon,
  DocumentTextIcon,
  UserIcon,
  PencilSquareIcon,
  TrashIcon,
  SparklesIcon,
  Squares2X2Icon,
} from '@heroicons/react/20/solid';

// ─── Constants ────────────────────────────────────────────────────────────────

const EndPoint = process.env.REACT_APP_API_ENDPOINT;

const PRIORITIES = ['high', 'moderate', 'low', 'inactive'];
const PRIORITY_ORDER = { high: 0, moderate: 1, low: 2, inactive: 3 };

const P = {
  high:     { label: 'High Priority',     short: 'High',     hdr: 'text-red-600',     cnt: 'bg-red-500',     dot: 'bg-red-500',     badge: 'bg-red-100 text-red-700',     drop: 'ring-2 ring-inset ring-red-300 bg-red-50',     hover: 'hover:bg-red-50',     plus: 'text-red-400 hover:text-red-600',     detailAccent: 'bg-red-500' },
  moderate: { label: 'Moderate Priority', short: 'Moderate', hdr: 'text-amber-500',   cnt: 'bg-amber-400',   dot: 'bg-amber-500',   badge: 'bg-amber-100 text-amber-700',   drop: 'ring-2 ring-inset ring-amber-300 bg-amber-50',   hover: 'hover:bg-amber-50',   plus: 'text-amber-400 hover:text-amber-600',   detailAccent: 'bg-amber-400' },
  low:      { label: 'Low Priority',      short: 'Low',      hdr: 'text-emerald-600', cnt: 'bg-emerald-500', dot: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700', drop: 'ring-2 ring-inset ring-emerald-300 bg-emerald-50', hover: 'hover:bg-emerald-50', plus: 'text-emerald-400 hover:text-emerald-600', detailAccent: 'bg-emerald-500' },
  inactive: { label: 'Inactive',          short: 'Inactive', hdr: 'text-gray-500',    cnt: 'bg-gray-400',    dot: 'bg-gray-300',    badge: 'bg-gray-100 text-gray-500',    drop: 'ring-2 ring-inset ring-gray-300 bg-gray-100',    hover: 'hover:bg-gray-50',    plus: 'text-gray-400 hover:text-gray-600',    detailAccent: 'bg-gray-400' },
};

const STATUS_STYLE = {
  'To do': 'bg-slate-100 text-slate-600 hover:bg-slate-200',
  'Doing': 'bg-amber-100 text-amber-700 hover:bg-amber-200',
  'Done':  'bg-emerald-100 text-emerald-700 hover:bg-emerald-200',
};

const STATUS_CYCLE = { 'To do': 'Doing', 'Doing': 'Done', 'Done': 'To do' };

// ─── Helpers ──────────────────────────────────────────────────────────────────

const todayISO = () => new Date().toISOString().slice(0, 10);

const fmt = (iso) => {
  if (!iso) return '';
  const p = (iso || '').split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
};

const calcAge = (dob) => {
  if (!dob) return null;
  const [y, m, d] = dob.split('-').map(Number);
  const n = new Date();
  let a = n.getFullYear() - y;
  if (n.getMonth() + 1 < m || (n.getMonth() + 1 === m && n.getDate() < d)) a--;
  return a;
};

const getInitials = (name) => {
  if (!name) return '?';
  const p = name.trim().split(/\s+/);
  return p.length >= 2 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
};

const condKey = (c, idx) => c?.id || `cond-${idx}`;

// Normalise any casing/spacing that the AI or other sources might return
const normPriority = (p) => {
  const v = String(p || '').toLowerCase().trim();
  return PRIORITIES.includes(v) ? v : 'moderate';
};

// normalise an entry (string or object) coming from the AI response
const normaliseEntry = (e, author, withStatus = true) => {
  const title = typeof e === 'string'
    ? e.trim()
    : String(e?.title || e?.text || '').trim();
  if (!title) return null;
  return {
    title,
    dateAdded: (typeof e === 'object' && e?.dateAdded) || todayISO(),
    author:    (typeof e === 'object' && e?.author)    || author || 'AI',
    ...(withStatus ? { status: (typeof e === 'object' && e?.status) || 'To do' } : {}),
  };
};

const normaliseCond = (c, author) => ({
  id:        c.id || (typeof crypto !== 'undefined' ? crypto.randomUUID() : `ai-${Date.now()}-${Math.random()}`),
  problem:   String(c.problem || '').trim(),
  priority:  normPriority(c.priority),
  dateAdded: c.dateAdded || todayISO(),
  author:    c.author    || author || 'AI',
  collapsed: false,
  notes:     (c.notes     || []).map(e => normaliseEntry(e, author, false)).filter(Boolean),
  goals:     (c.goals     || []).map(e => normaliseEntry(e, author)).filter(Boolean),
  tasks:     (c.tasks     || []).map(e => normaliseEntry(e, author)).filter(Boolean),
  referrals: (c.referrals || []).map(e => normaliseEntry(e, author)).filter(Boolean),
});

// ─── Panel wrapper ────────────────────────────────────────────────────────────

function DetailPanel({ title, count, countLabel, children, icon: Icon }) {
  return (
    <div className="p-4 flex flex-col gap-2 min-w-0 overflow-hidden">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5">
          {Icon && <Icon className="h-4 w-4 text-slate-400 flex-shrink-0" />}
          <h4 className="text-sm font-bold text-slate-700 truncate">{title}</h4>
        </div>
        {count !== undefined && (
          <span className="text-[10px] text-slate-400 flex-shrink-0 ml-2">{count} {countLabel}</span>
        )}
      </div>
      {children}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

class CarePlan extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      // selection / search
      selectedId:       null,
      searchQuery:      '',
      // drag & drop
      dragItemId:       null,
      dragOverPriority: null,
      // notes add
      updateDraft:      '',
      // condition card menu
      menuOpenId:       null,
      // inline item editing
      editingItem:      null,   // { section, origIdx, draft }
      addDrafts:        { goals: '', tasks: '', referrals: '' },
      // problem name editing
      editingProblem:   false,
      problemDraft:     '',
      // ── Smart Paste / AI ──
      smartPasteOpen:   false,
      smartPasteText:   '',
      smartPasteBusy:   false,
      smartPasteError:  '',
      // ── Merge ──
      mergeOpen:        false,
      mergeSelected:    new Set(),   // Set of condKey strings
      mergeTitle:       '',
    };
    this.menuRef = React.createRef();
    this._toastId = null;
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleOutsideClick);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleOutsideClick);
  }

  handleOutsideClick = (e) => {
    if (this.menuRef.current && !this.menuRef.current.contains(e.target)) {
      this.setState({ menuOpenId: null });
    }
  };

  // ── Drag & Drop ──────────────────────────────────────────────────────────

  onDragStart = (e, key) => {
    this.setState({ dragItemId: key });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', key);
  };

  onDragOver = (e, priority) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (this.state.dragOverPriority !== priority) this.setState({ dragOverPriority: priority });
  };

  onDrop = (e, priority) => {
    e.preventDefault();
    const { dragItemId } = this.state;
    const { conditions = [], onUpdateConditions } = this.props;
    if (!dragItemId || !onUpdateConditions) return;
    const next = conditions.map((c, i) =>
      condKey(c, i) === dragItemId ? { ...c, priority } : c
    );
    onUpdateConditions(next);
    this.setState({ dragItemId: null, dragOverPriority: null });
  };

  onDragEnd = () => this.setState({ dragItemId: null, dragOverPriority: null });

  onDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) this.setState({ dragOverPriority: null });
  };

  // ── Selection ────────────────────────────────────────────────────────────

  selectCondition = (key) => {
    this.setState(s => ({
      selectedId:     s.selectedId === key ? null : key,
      updateDraft:    '',
      menuOpenId:     null,
      editingItem:    null,
      addDrafts:      { goals: '', tasks: '', referrals: '' },
      editingProblem: false,
      problemDraft:   '',
    }));
  };

  getSelected = () => {
    const { conditions = [] } = this.props;
    const { selectedId } = this.state;
    return conditions.find((c, i) => condKey(c, i) === selectedId) || null;
  };

  // ── Problem name ─────────────────────────────────────────────────────────

  saveProblem = () => {
    const { problemDraft, selectedId } = this.state;
    const { conditions = [], onUpdateConditions } = this.props;
    if (!problemDraft.trim() || !selectedId || !onUpdateConditions) return;
    const next = conditions.map((c, i) =>
      condKey(c, i) === selectedId ? { ...c, problem: problemDraft.trim() } : c
    );
    onUpdateConditions(next);
    this.setState({ editingProblem: false, problemDraft: '' });
  };

  // ── CRUD helpers ─────────────────────────────────────────────────────────

  cycleStatus = (section, origIdx) => {
    const { selectedId } = this.state;
    const { conditions = [], onUpdateConditions } = this.props;
    if (!selectedId || !onUpdateConditions) return;
    const next = conditions.map((c, i) => {
      if (condKey(c, i) !== selectedId) return c;
      const items = [...(c[section] || [])];
      const cur = items[origIdx] || {};
      items[origIdx] = { ...cur, status: STATUS_CYCLE[cur.status || 'To do'] };
      return { ...c, [section]: items };
    });
    onUpdateConditions(next);
  };

  startEdit = (section, origIdx, title) => {
    this.setState({ editingItem: { section, origIdx, draft: title || '' } });
  };

  saveEdit = () => {
    const { editingItem, selectedId } = this.state;
    const { conditions = [], onUpdateConditions } = this.props;
    if (!editingItem || !selectedId || !onUpdateConditions) return;
    const { section, origIdx, draft } = editingItem;
    if (!draft.trim()) return;
    const now = todayISO();
    const next = conditions.map((c, i) => {
      if (condKey(c, i) !== selectedId) return c;
      const items = [...(c[section] || [])];
      items[origIdx] = { ...items[origIdx], title: draft.trim(), updatedAt: now, updatedAtMs: Date.now() };
      return { ...c, [section]: items };
    });
    onUpdateConditions(next);
    this.setState({ editingItem: null });
  };

  cancelEdit = () => this.setState({ editingItem: null });

  deleteItem = (section, origIdx) => {
    const { selectedId } = this.state;
    const { conditions = [], onUpdateConditions } = this.props;
    if (!selectedId || !onUpdateConditions) return;
    const next = conditions.map((c, i) => {
      if (condKey(c, i) !== selectedId) return c;
      const items = [...(c[section] || [])];
      items.splice(origIdx, 1);
      return { ...c, [section]: items };
    });
    onUpdateConditions(next);
    const { editingItem } = this.state;
    if (editingItem?.section === section && editingItem?.origIdx === origIdx) {
      this.setState({ editingItem: null });
    }
  };

  addItem = (section) => {
    const { addDrafts, selectedId } = this.state;
    const { conditions = [], onUpdateConditions, gpName } = this.props;
    const draft = (addDrafts[section] || '').trim();
    if (!draft || !selectedId || !onUpdateConditions) return;
    const now = todayISO();
    const entry = {
      title: draft, dateAdded: now, updatedAt: now,
      createdAtMs: Date.now(), updatedAtMs: Date.now(),
      author: gpName || 'System', status: 'To do',
    };
    const next = conditions.map((c, i) => {
      if (condKey(c, i) !== selectedId) return c;
      return { ...c, [section]: [...(c[section] || []), entry] };
    });
    onUpdateConditions(next);
    this.setState({ addDrafts: { ...addDrafts, [section]: '' } });
  };

  addNote = () => {
    const { updateDraft, selectedId } = this.state;
    const { conditions = [], onUpdateConditions, gpName } = this.props;
    if (!updateDraft.trim() || !selectedId || !onUpdateConditions) return;
    const note = { title: updateDraft.trim(), dateAdded: todayISO(), author: gpName || 'System' };
    const next = conditions.map((c, i) =>
      condKey(c, i) === selectedId ? { ...c, notes: [note, ...(c.notes || [])] } : c
    );
    onUpdateConditions(next);
    this.setState({ updateDraft: '' });
  };

  // ── Smart Paste / AI ─────────────────────────────────────────────────────

  openSmartPaste = () =>
    this.setState({ smartPasteOpen: true, smartPasteText: '', smartPasteError: '' });

  closeSmartPaste = () => {
    if (this.state.smartPasteBusy) return;
    this.setState({ smartPasteOpen: false, smartPasteText: '', smartPasteError: '' });
  };

  // Called by <Transcribe> when voice recording finishes
  handleTranscription = (data) => {
    const transcription = data?.transcription || '';
    if (data?.output) {
      const { amount, newBalance, reason } = data.output;
      toast.info(`${amount} credits used to ${reason}`, { autoClose: 1800 });
      this.props.onCreditsUpdate?.(newBalance);
    }
    // Open smart paste modal with transcript pre-filled
    this.setState({ smartPasteOpen: true, smartPasteText: transcription, smartPasteError: '' });
  };

  handleSmartPaste = async () => {
    const { smartPasteText } = this.state;
    const { conditions = [], onUpdateConditions, gpName } = this.props;

    if (!smartPasteText.trim()) {
      this.setState({ smartPasteError: 'Please paste or type something first.' });
      return;
    }

    this.setState({ smartPasteBusy: true, smartPasteError: '' });
    this._toastId = toast.loading('Processing with AI…');

    try {
      const res = await axios.post(
        `${EndPoint}/careplan/smart-paste`,
        {
          input: smartPasteText,
          existingConditions: conditions.map(c => ({
            problem:   c.problem,
            priority:  c.priority,
            collapsed: !!c.collapsed,
            notes:     (c.notes     || []).map(e => typeof e === 'string' ? e : e?.title || ''),
            goals:     (c.goals     || []).map(e => typeof e === 'string' ? e : e?.title || ''),
            tasks:     (c.tasks     || []).map(e => typeof e === 'string' ? e : e?.title || ''),
            referrals: (c.referrals || []).map(e => typeof e === 'string' ? e : e?.title || ''),
          })),
        },
        { withCredentials: true }
      );

      const payload = res?.data;
      if (payload?.output) {
        const { amount, newBalance, reason } = payload.output;
        toast.info(`${amount} credits used to ${reason}`, { autoClose: 1800 });
        this.props.onCreditsUpdate?.(newBalance);
      }

      if (Array.isArray(payload?.conditions)) {
        const author = gpName || 'AI';
        const normalised = payload.conditions.map(c => normaliseCond(c, author));
        // sort by priority order
        normalised.sort((a, b) =>
          (PRIORITY_ORDER[a.priority] ?? 1) - (PRIORITY_ORDER[b.priority] ?? 1)
        );
        onUpdateConditions?.(normalised);
      }

      toast.update(this._toastId, {
        render: 'Plan updated', type: 'success', isLoading: false, autoClose: 1500,
      });

      this.setState({ smartPasteBusy: false, smartPasteOpen: false, smartPasteText: '' });
    } catch (err) {
      console.error(err);
      toast.update(this._toastId, {
        render: err?.response?.data?.error || err?.message || 'Request failed',
        type: 'error', isLoading: false, autoClose: 3000,
      });
      this.setState({
        smartPasteBusy: false,
        smartPasteError: err?.response?.data?.error || err?.message || 'Request failed',
      });
    }
  };

  // ── Merge ─────────────────────────────────────────────────────────────────

  openMergeModal = () =>
    this.setState({ mergeOpen: true, mergeSelected: new Set(), mergeTitle: '' });

  closeMergeModal = () => this.setState({ mergeOpen: false });

  toggleMergeSelect = (key) => {
    this.setState(s => {
      const next = new Set(s.mergeSelected);
      if (next.has(key)) next.delete(key); else next.add(key);
      return { mergeSelected: next };
    });
  };

  _pickMergedPriority = (conds) => {
    let best = Infinity, bestKey = 'moderate';
    for (const c of conds) {
      const v = PRIORITY_ORDER[normPriority(c?.priority)] ?? 1;
      if (v < best) { best = v; bestKey = c?.priority || 'moderate'; }
    }
    return bestKey;
  };

  _mergeActionLists = (...lists) => {
    const out = [], seen = new Set();
    for (const arr of lists) {
      for (const e of (Array.isArray(arr) ? arr : [])) {
        const title = typeof e === 'string' ? e.trim() : String(e?.title || '').trim();
        if (!title) continue;
        const key = title.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(typeof e === 'string' ? { title, dateAdded: todayISO(), author: 'Merged' } : { ...e });
      }
    }
    return out;
  };

  performMerge = () => {
    const { mergeTitle, mergeSelected } = this.state;
    const { conditions = [], onUpdateConditions } = this.props;
    const name = (mergeTitle || '').trim();

    if (!name) { toast.error('Enter a name for the merged problem.'); return; }
    if (!mergeSelected.size) { toast.error('Select at least one problem to merge.'); return; }

    const picked = conditions.filter((c, i) => mergeSelected.has(condKey(c, i)));

    const merged = {
      id:        crypto.randomUUID(),
      problem:   name,
      priority:  this._pickMergedPriority(picked),
      dateAdded: todayISO(),
      author:    this.props.gpName || 'Merged',
      collapsed: false,
      notes:     this._mergeActionLists(...picked.map(c => c?.notes     || [])),
      goals:     this._mergeActionLists(...picked.map(c => c?.goals     || [])),
      tasks:     this._mergeActionLists(...picked.map(c => c?.tasks     || [])),
      referrals: this._mergeActionLists(...picked.map(c => c?.referrals || [])),
    };

    const remaining = conditions.filter((c, i) => !mergeSelected.has(condKey(c, i)));
    onUpdateConditions?.([merged, ...remaining]);
    this.setState({ mergeOpen: false, mergeSelected: new Set(), mergeTitle: '' });
    toast.success('Problems merged.');
  };

  // ── Data helpers ─────────────────────────────────────────────────────────

  counts = () => {
    const { conditions = [] } = this.props;
    const c = { high: 0, moderate: 0, low: 0, inactive: 0 };
    conditions.forEach(x => { const p = normPriority(x.priority); c[p]++; });
    return c;
  };

  byPriority = (priority) => {
    const { conditions = [] } = this.props;
    const q = (this.state.searchQuery || '').toLowerCase().trim();
    return conditions
      .map((c, i) => ({ ...c, _key: condKey(c, i) }))
      .filter(c =>
        normPriority(c.priority) === priority &&
        (!q || (c.problem || '').toLowerCase().includes(q))
      );
  };

  // ── Shared editable list renderer ─────────────────────────────────────────

  renderItemList({ items, section, label, renderIcon, withStatus = true }) {
    const { editingItem, addDrafts } = this.state;
    const addDraft = addDrafts[section] || '';

    return (
      <div className="flex flex-col">
        <div className="overflow-y-auto max-h-56">
          {items.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-5">No {label.toLowerCase()}s yet.</p>
          )}
          {items.map((item, origIdx) => {
            const isEditing = editingItem?.section === section && editingItem?.origIdx === origIdx;
            const st = item.status || 'To do';

            if (isEditing) {
              return (
                <div key={origIdx} className="py-2.5 border-b border-slate-100">
                  <textarea
                    autoFocus rows={2}
                    className="w-full text-sm rounded-xl border border-slate-300 px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-sky-200"
                    value={editingItem.draft}
                    onChange={e => this.setState({ editingItem: { ...editingItem, draft: e.target.value } })}
                    onKeyDown={e => {
                      if (e.key === 'Escape') this.cancelEdit();
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) this.saveEdit();
                    }}
                  />
                  <div className="flex gap-2 mt-1.5">
                    <button type="button" className="px-2.5 py-1 bg-emerald-600 text-white text-xs rounded-xl font-semibold hover:bg-emerald-700 transition" onClick={this.saveEdit}>Save</button>
                    <button type="button" className="px-2.5 py-1 bg-slate-100 text-slate-700 text-xs rounded-xl font-semibold hover:bg-slate-200 transition" onClick={this.cancelEdit}>Cancel</button>
                  </div>
                </div>
              );
            }

            return (
              <div key={origIdx} className="flex items-start gap-2 py-2.5 border-b border-slate-100 last:border-0 group">
                <div className="flex-shrink-0 mt-0.5">{renderIcon(st)}</div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm leading-snug ${withStatus && st === 'Done' ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                    {item.title || item}
                  </p>
                  {(item.updatedAt || item.dateAdded) && (
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {item.updatedAt ? `Updated ${fmt(item.updatedAt)}` : `Added ${fmt(item.dateAdded)}`}
                      {item.author && item.author !== 'AI' && item.author !== 'System' ? ` · ${item.author}` : ''}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button type="button" title="Edit" className="p-1 rounded-lg hover:bg-slate-100 transition" onClick={() => this.startEdit(section, origIdx, item.title || '')}>
                    <PencilSquareIcon className="h-3.5 w-3.5 text-slate-400" />
                  </button>
                  <button type="button" title="Delete" className="p-1 rounded-lg hover:bg-red-50 transition" onClick={() => this.deleteItem(section, origIdx)}>
                    <TrashIcon className="h-3.5 w-3.5 text-red-400" />
                  </button>
                </div>
                {withStatus && (
                  <button
                    type="button"
                    title="Click to cycle status"
                    onClick={() => this.cycleStatus(section, origIdx)}
                    className={`flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded-full font-semibold transition active:scale-95 ${STATUS_STYLE[st] || STATUS_STYLE['To do']}`}
                  >
                    {st}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <div className="mt-2 pt-2 border-t border-slate-100">
          <textarea
            rows={2}
            className="w-full text-sm rounded-xl border border-slate-200 px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300"
            placeholder={`Add ${label.toLowerCase()}…`}
            value={addDraft}
            onChange={e => this.setState({ addDrafts: { ...this.state.addDrafts, [section]: e.target.value } })}
            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) this.addItem(section); }}
          />
          <button type="button" className="mt-1.5 w-full rounded-xl bg-slate-700 text-white text-xs font-semibold py-1.5 hover:bg-slate-800 transition" onClick={() => this.addItem(section)}>
            + Add {label}
          </button>
        </div>
      </div>
    );
  }

  // ── Notes / progress section ──────────────────────────────────────────────

  renderNotesSection(c) {
    const { updateDraft, editingItem } = this.state;
    const notesWithIdx = (c.notes || [])
      .map((n, origIdx) => ({ ...n, origIdx }))
      .sort((a, b) => (b.dateAdded || '').localeCompare(a.dateAdded || ''));

    return (
      <div className="flex flex-col gap-2">
        <textarea
          rows={3}
          className="w-full text-sm rounded-xl border border-slate-200 px-3 py-2 text-slate-700 resize-none focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-300"
          placeholder="Add update…"
          value={updateDraft}
          onChange={e => this.setState({ updateDraft: e.target.value })}
          onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) this.addNote(); }}
        />
        <button type="button" className="self-end px-3 py-1.5 rounded-xl bg-sky-700 text-white text-xs font-semibold hover:bg-sky-800 transition" onClick={this.addNote}>
          Post
        </button>
        <div className="overflow-y-auto max-h-48 divide-y divide-slate-50">
          {notesWithIdx.length === 0 && (
            <p className="text-xs text-slate-400 text-center py-4">No updates yet.</p>
          )}
          {notesWithIdx.map((n) => {
            const { origIdx } = n;
            const isEditing = editingItem?.section === 'notes' && editingItem?.origIdx === origIdx;
            if (isEditing) {
              return (
                <div key={origIdx} className="py-2">
                  <textarea autoFocus rows={2}
                    className="w-full text-sm rounded-xl border border-slate-300 px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-sky-200"
                    value={editingItem.draft}
                    onChange={e => this.setState({ editingItem: { ...editingItem, draft: e.target.value } })}
                    onKeyDown={e => {
                      if (e.key === 'Escape') this.cancelEdit();
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) this.saveEdit();
                    }}
                  />
                  <div className="flex gap-2 mt-1.5">
                    <button type="button" className="px-2.5 py-1 bg-emerald-600 text-white text-xs rounded-xl font-semibold hover:bg-emerald-700" onClick={this.saveEdit}>Save</button>
                    <button type="button" className="px-2.5 py-1 bg-slate-100 text-slate-700 text-xs rounded-xl font-semibold hover:bg-slate-200" onClick={this.cancelEdit}>Cancel</button>
                  </div>
                </div>
              );
            }
            return (
              <div key={origIdx} className="flex items-start gap-2 py-2 group">
                <div className="w-1.5 h-1.5 rounded-full bg-sky-400 mt-1.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-700 leading-snug">{n.title || n}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    {fmt(n.dateAdded)}{n.author && n.author !== 'AI' && n.author !== 'System' ? ` · ${n.author}` : ''}
                  </p>
                </div>
                <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button type="button" title="Edit" className="p-1 rounded-lg hover:bg-slate-100 transition" onClick={() => this.startEdit('notes', origIdx, n.title || '')}>
                    <PencilSquareIcon className="h-3.5 w-3.5 text-slate-400" />
                  </button>
                  <button type="button" title="Delete" className="p-1 rounded-lg hover:bg-red-50 transition" onClick={() => this.deleteItem('notes', origIdx)}>
                    <TrashIcon className="h-3.5 w-3.5 text-red-400" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Priority column ───────────────────────────────────────────────────────

  renderDot(priority) {
    if (priority === 'inactive') {
      return (
        <span className="w-3 h-3 rounded-full border-2 border-gray-400 bg-white flex-shrink-0 inline-flex items-center justify-center">
          <span className="w-1.5 h-0.5 bg-gray-400 rounded" />
        </span>
      );
    }
    return <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${P[priority].dot}`} />;
  }

  renderConditionItem(c, priority) {
    const cfg       = P[normPriority(priority)] || P.moderate;
    const isSelected = this.state.selectedId === c._key;
    const isDragging = this.state.dragItemId === c._key;
    const menuOpen   = this.state.menuOpenId === c._key;

    return (
      <div
        key={c._key}
        draggable
        onDragStart={(e) => this.onDragStart(e, c._key)}
        onDragEnd={this.onDragEnd}
        onClick={() => this.selectCondition(c._key)}
        className={`
          relative flex items-center justify-between px-3 py-2.5 rounded-xl border
          cursor-pointer select-none transition-all
          ${isSelected ? 'border-sky-400 bg-sky-50 shadow' : `border-slate-200 bg-white shadow-sm ${cfg.hover}`}
          ${isDragging ? 'opacity-30 scale-95' : ''}
        `}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {this.renderDot(priority)}
          <span className="text-sm font-medium text-slate-800 truncate">{c.problem || 'Unnamed'}</span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {c.category && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 whitespace-nowrap hidden lg:inline">
              {c.category}
            </span>
          )}
          <div className="relative" ref={menuOpen ? this.menuRef : null}>
            <button
              type="button"
              className="p-1 rounded-lg hover:bg-slate-100 text-slate-400 transition"
              onClick={(e) => {
                e.stopPropagation();
                this.setState(s => ({ menuOpenId: s.menuOpenId === c._key ? null : c._key }));
              }}
            >
              <EllipsisVerticalIcon className="h-4 w-4" />
            </button>
            {menuOpen && (
              <div
                className="absolute right-0 top-6 z-30 w-40 rounded-xl border border-slate-200 bg-white shadow-xl py-1"
                onClick={e => e.stopPropagation()}
              >
                {PRIORITIES.filter(p => p !== priority).map(p => (
                  <button key={p} type="button"
                    className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    onClick={() => {
                      const { conditions = [], onUpdateConditions } = this.props;
                      if (!onUpdateConditions) return;
                      onUpdateConditions(conditions.map((x, i) => condKey(x, i) === c._key ? { ...x, priority: p } : x));
                      this.setState({ menuOpenId: null });
                    }}
                  >
                    <span className={`w-2 h-2 rounded-full ${P[p].dot}`} />
                    Move to {P[p].short}
                  </button>
                ))}
                <div className="border-t border-slate-100 my-1" />
                <button type="button"
                  className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50"
                  onClick={() => {
                    const { conditions = [], onUpdateConditions } = this.props;
                    if (!onUpdateConditions) return;
                    onUpdateConditions(conditions.filter((x, i) => condKey(x, i) !== c._key));
                    if (this.state.selectedId === c._key) this.setState({ selectedId: null });
                    this.setState({ menuOpenId: null });
                  }}
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  renderPriorityColumn(priority) {
    const cfg        = P[priority];
    const items      = this.byPriority(priority);
    const isDropOver = this.state.dragOverPriority === priority && this.state.dragItemId;

    return (
      <div
        key={priority}
        className={`flex flex-col rounded-2xl border transition-all ${isDropOver ? cfg.drop : 'border-slate-200 bg-white'}`}
        onDragOver={(e) => this.onDragOver(e, priority)}
        onDrop={(e) => this.onDrop(e, priority)}
        onDragLeave={this.onDragLeave}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <span className={`text-sm font-bold ${cfg.hdr}`}>{cfg.label}</span>
            <span className={`text-xs font-bold text-white px-1.5 py-0.5 rounded-full min-w-[20px] text-center ${cfg.cnt}`}>{items.length}</span>
          </div>
          <button type="button" title={`Add ${cfg.short} problem`} className={`${cfg.plus} transition rounded-lg p-0.5`} onClick={() => this.props.onAddCondition?.(priority)}>
            <PlusIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="flex flex-col gap-2 p-3 min-h-[100px] flex-1">
          {items.length === 0 && (
            <div className={`flex-1 flex items-center justify-center py-8 rounded-xl border border-dashed text-xs transition-all ${isDropOver ? 'border-slate-400 text-slate-500' : 'border-slate-200 text-slate-300'}`}>
              {isDropOver ? 'Drop here' : 'None'}
            </div>
          )}
          {items.map(c => this.renderConditionItem(c, priority))}
        </div>
      </div>
    );
  }

  // ── Detail panel ──────────────────────────────────────────────────────────

  renderDetailPanel(c) {
    const cfg       = P[normPriority(c.priority)];
    const goals     = c.goals     || [];
    const tasks     = c.tasks     || [];
    const referrals = c.referrals || [];
    const doneGoals  = goals.filter(g => (g.status || 'To do') === 'Done').length;
    const activeTasks = tasks.filter(t => (t.status || 'To do') !== 'Done').length;

    return (
      <div className="mt-3 rounded-2xl border border-slate-200 bg-white shadow-md overflow-hidden">
        {/* header */}
        <div className="flex items-center justify-between px-5 py-4 bg-slate-50 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${cfg.detailAccent}`}>
              <UserIcon className="h-4 w-4 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                {this.state.editingProblem ? (
                  <div className="flex items-center gap-2">
                    <input
                      autoFocus type="text"
                      className="text-base font-bold rounded-xl border border-slate-300 px-3 py-1 text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-200 w-64"
                      value={this.state.problemDraft}
                      onChange={e => this.setState({ problemDraft: e.target.value })}
                      onKeyDown={e => {
                        if (e.key === 'Enter') this.saveProblem();
                        if (e.key === 'Escape') this.setState({ editingProblem: false, problemDraft: '' });
                      }}
                    />
                    <button type="button" className="px-2.5 py-1 bg-emerald-600 text-white text-xs rounded-xl font-semibold hover:bg-emerald-700 transition" onClick={this.saveProblem}>Save</button>
                    <button type="button" className="px-2.5 py-1 bg-slate-100 text-slate-700 text-xs rounded-xl font-semibold hover:bg-slate-200 transition" onClick={() => this.setState({ editingProblem: false, problemDraft: '' })}>Cancel</button>
                  </div>
                ) : (
                  <button type="button" className="group flex items-center gap-1.5 text-left" title="Click to edit problem name"
                    onClick={() => this.setState({ editingProblem: true, problemDraft: c.problem || '' })}
                  >
                    <h3 className="text-base font-bold text-slate-800 group-hover:text-sky-700 transition">{c.problem || 'Unnamed'}</h3>
                    <PencilSquareIcon className="h-3.5 w-3.5 text-slate-300 group-hover:text-sky-500 transition flex-shrink-0" />
                  </button>
                )}
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cfg.badge}`}>{cfg.short} Priority</span>
              </div>
              <div className="flex items-center gap-4 text-xs text-slate-500 mt-0.5 flex-wrap">
                <span>Problem since: <strong>{fmt(c.dateAdded) || '—'}</strong></span>
                {this.props.lastGPCCMPDate && <span>Last review: <strong>{fmt(this.props.lastGPCCMPDate)}</strong></span>}
                {this.props.proposedReviewDate && <span>Next review: <strong>{fmt(this.props.proposedReviewDate)}</strong></span>}
                {c.author && <span>Added by: <strong>{c.author}</strong></span>}
              </div>
            </div>
          </div>
          <button type="button" onClick={() => this.setState({ selectedId: null, editingItem: null, editingProblem: false, problemDraft: '' })} className="p-2 rounded-xl hover:bg-slate-200 text-slate-400 transition flex-shrink-0">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* 4-column body */}
        <div className="grid grid-cols-4 divide-x divide-slate-100">
          <DetailPanel title="SMART Goals" icon={CheckCircleIcon} count={`${doneGoals}/${goals.length}`} countLabel="done">
            {this.renderItemList({
              items: goals, section: 'goals', label: 'Goal',
              renderIcon: (st) => (
                <CheckCircleIcon className={`h-4 w-4 ${st === 'Done' ? 'text-emerald-500' : st === 'Doing' ? 'text-amber-400' : 'text-slate-200'}`} />
              ),
            })}
          </DetailPanel>

          <DetailPanel title="Progress / Updates" icon={ArrowPathIcon}>
            {this.renderNotesSection(c)}
          </DetailPanel>

          <DetailPanel title="Tasks & Treatments" icon={ClockIcon} count={activeTasks} countLabel="active">
            {this.renderItemList({
              items: tasks, section: 'tasks', label: 'Task',
              renderIcon: (st) => (
                <div className={`w-4 h-4 rounded border-2 flex-shrink-0 ${st === 'Done' ? 'bg-emerald-500 border-emerald-500' : st === 'Doing' ? 'bg-amber-400 border-amber-400' : 'border-slate-300 bg-white'}`} />
              ),
            })}
          </DetailPanel>

          <DetailPanel title="Referrals & Arrangements" icon={DocumentTextIcon} count={referrals.length} countLabel="total">
            {this.renderItemList({
              items: referrals, section: 'referrals', label: 'Referral',
              renderIcon: () => <UserIcon className="h-4 w-4 text-purple-400" />,
            })}
          </DetailPanel>
        </div>
      </div>
    );
  }

  // ── Smart Paste modal ─────────────────────────────────────────────────────

  renderSmartPasteModal() {
    if (!this.state.smartPasteOpen) return null;
    const { smartPasteText, smartPasteBusy, smartPasteError } = this.state;

    return (
      <div
        role="dialog" aria-modal="true"
        className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/50 backdrop-blur-sm px-4"
        onMouseDown={e => { if (e.target === e.currentTarget) this.closeSmartPaste(); }}
      >
        <div
          className="w-full max-w-3xl rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden"
          onMouseDown={e => e.stopPropagation()}
        >
          {/* header */}
          <div className="bg-gradient-to-r from-sky-900 via-sky-800 to-cyan-700 px-6 py-5 text-white flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <SparklesIcon className="h-5 w-5 text-yellow-300" />
                <h3 className="text-xl font-bold tracking-tight">AI Write / Smart Paste</h3>
              </div>
              <p className="mt-1 text-sm text-white/80">
                Paste or dictate unstructured notes — AI will organise them into the care plan.
              </p>
            </div>
            <button type="button" className="mt-1 p-1.5 rounded-xl bg-white/15 hover:bg-white/25 transition" onClick={this.closeSmartPaste} disabled={smartPasteBusy}>
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* body */}
          <div className="p-6 space-y-4">
            <div className="rounded-2xl border border-sky-100 bg-sky-50/70 px-4 py-3 text-sm text-slate-600">
              You can paste messy consultation notes, bullet points, or dictated text. AI will extract conditions, goals, tasks and referrals and merge them into the existing care plan.
            </div>

            <textarea
              className="h-72 w-full resize-none rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200"
              placeholder="Paste or type notes here…"
              value={smartPasteText}
              onChange={e => this.setState({ smartPasteText: e.target.value, smartPasteError: '' })}
              disabled={smartPasteBusy}
            />

            {smartPasteError && (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{smartPasteError}</div>
            )}

            <div className="flex justify-end gap-3">
              <button type="button"
                className="rounded-2xl bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-200 transition"
                onClick={this.closeSmartPaste} disabled={smartPasteBusy}
              >
                Cancel
              </button>
              <button type="button"
                className={`rounded-2xl px-5 py-2.5 text-sm font-semibold text-white shadow-md transition ${
                  smartPasteBusy ? 'bg-slate-400 cursor-not-allowed' : 'bg-gradient-to-r from-sky-700 to-cyan-700 hover:from-sky-800 hover:to-cyan-800'
                }`}
                onClick={this.handleSmartPaste} disabled={smartPasteBusy}
              >
                {smartPasteBusy ? 'Processing…' : 'Submit to AI'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Merge modal ───────────────────────────────────────────────────────────

  renderMergeModal() {
    if (!this.state.mergeOpen) return null;
    const { mergeSelected, mergeTitle } = this.state;
    const { conditions = [] } = this.props;

    return (
      <div
        role="dialog" aria-modal="true"
        className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/50 backdrop-blur-sm px-4"
        onMouseDown={e => { if (e.target === e.currentTarget) this.closeMergeModal(); }}
      >
        <div
          className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden"
          onMouseDown={e => e.stopPropagation()}
        >
          {/* header */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-6 py-5 text-white flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Squares2X2Icon className="h-5 w-5 text-white/70" />
                <h3 className="text-lg font-bold tracking-tight">Merge Problems</h3>
              </div>
              <p className="mt-1 text-sm text-white/70">Select problems to merge, then give the combined problem a name.</p>
            </div>
            <button type="button" className="p-1.5 rounded-xl bg-white/15 hover:bg-white/25 transition" onClick={this.closeMergeModal}>
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* body */}
          <div className="p-6 space-y-4">
            {/* problem checklist */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {conditions.length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">No conditions to merge.</p>
              )}
              {conditions.map((c, i) => {
                const key = condKey(c, i);
                const checked = mergeSelected.has(key);
                const cfg = P[normPriority(c.priority)];
                return (
                  <label key={key} className={`flex items-center gap-3 px-4 py-3 rounded-2xl border cursor-pointer transition ${checked ? 'border-sky-400 bg-sky-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}>
                    <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-400"
                      checked={checked}
                      onChange={() => this.toggleMergeSelect(key)}
                    />
                    <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${cfg.dot}`} />
                    <span className="flex-1 text-sm font-medium text-slate-800 truncate">{c.problem || 'Unnamed'}</span>
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${cfg.badge}`}>{cfg.short}</span>
                  </label>
                );
              })}
            </div>

            {/* merged name input */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">Merged problem name</label>
              <input
                type="text"
                className="w-full rounded-2xl border border-slate-300 px-4 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-200 focus:border-sky-400"
                placeholder="e.g. Metabolic Syndrome"
                value={mergeTitle}
                onChange={e => this.setState({ mergeTitle: e.target.value })}
                onKeyDown={e => { if (e.key === 'Enter') this.performMerge(); }}
              />
            </div>

            {mergeSelected.size > 0 && (
              <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-2.5 text-xs text-amber-700">
                {mergeSelected.size} problem{mergeSelected.size > 1 ? 's' : ''} will be merged and removed. Their goals, tasks and referrals will be combined (duplicates removed).
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button type="button" className="rounded-2xl bg-slate-100 px-4 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-200 transition" onClick={this.closeMergeModal}>
                Cancel
              </button>
              <button type="button"
                className="rounded-2xl bg-slate-800 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-900 transition"
                onClick={this.performMerge}
              >
                Merge
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────

  render() {
    const {
      ptFullName = '', ptDoB = '', ptPhoneMob = '', isATSI = '',
      proposedReviewDate = '', lastGPCCMPDate = '', usualGP = '', gpName = '',
    } = this.props;

    const { searchQuery } = this.state;
    const cnts     = this.counts();
    const total    = Object.values(cnts).reduce((a, b) => a + b, 0);
    const selected = this.getSelected();
    const ptAge    = calcAge(ptDoB);

    return (
      <div className="bg-slate-50 p-4 space-y-4">

        {/* ── Page header ── */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Care Plan Overview</h1>
            <p className="text-sm text-slate-500">Manage and track patient problems by priority</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                className="pl-9 pr-3 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-200 w-48"
                placeholder="Search problems…"
                value={searchQuery}
                onChange={e => this.setState({ searchQuery: e.target.value })}
              />
            </div>
            <button type="button" className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white text-sm text-slate-600 hover:bg-slate-50 transition">
              <FunnelIcon className="h-4 w-4" />Filter
            </button>
            <button
              type="button"
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-sky-700 text-white text-sm font-semibold hover:bg-sky-800 transition shadow-sm"
              onClick={() => this.props.onAddCondition?.('moderate')}
            >
              <PlusIcon className="h-4 w-4" />Add Problem
            </button>
          </div>
        </div>

        {/* ── Patient card ── */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-5 py-4 flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-lg flex-shrink-0 shadow-sm">
              {getInitials(ptFullName)}
            </div>
            <div>
              <div className="font-bold text-slate-800 text-base leading-tight">
                {ptFullName || <span className="text-slate-400 font-normal italic">No patient loaded</span>}
              </div>
              <div className="text-sm text-slate-500">
                {ptDoB && `${fmt(ptDoB)} (${ptAge} y/o)`}
                {isATSI === 'yes' && <span className="ml-2">· ATSI</span>}
              </div>
              {ptPhoneMob && <div className="text-sm text-slate-400">{ptPhoneMob}</div>}
            </div>
          </div>

          <div className="h-10 w-px bg-slate-200 hidden sm:block" />

          {ptDoB && (
            <div>
              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">DOB</div>
              <div className="text-sm font-semibold text-slate-700">{fmt(ptDoB)} ({ptAge} y/o)</div>
            </div>
          )}
          {proposedReviewDate && (
            <div>
              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Next Review</div>
              <div className="text-sm font-semibold text-slate-700">{fmt(proposedReviewDate)}</div>
            </div>
          )}
          {lastGPCCMPDate && (
            <div>
              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Care Plan Start</div>
              <div className="text-sm font-semibold text-slate-700">{fmt(lastGPCCMPDate)}</div>
            </div>
          )}
          {(usualGP || gpName) && (
            <div>
              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Usual GP</div>
              <div className="text-sm font-semibold text-slate-700">{usualGP || gpName}</div>
            </div>
          )}

          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-500 hidden sm:inline">Active Problems</span>
            {PRIORITIES.map(p => cnts[p] > 0 && (
              <span key={p} title={P[p].label} className={`text-xs font-bold text-white w-7 h-7 rounded-full flex items-center justify-center shadow-sm ${P[p].cnt}`}>
                {cnts[p]}
              </span>
            ))}
            <span className="text-sm font-bold text-slate-700">Total {total}</span>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className="flex items-center gap-2 px-1 flex-wrap">
          <AudioRecorder compact onInputUpdate={this.handleTranscription} />

          <button
            type="button"
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 bg-white shadow-sm text-sm font-medium text-slate-600 hover:bg-slate-50 transition"
            onClick={this.openMergeModal}
          >
            <Squares2X2Icon className="h-4 w-4 text-slate-500" />
            Merge Problems
          </button>

          <button
            type="button"
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-sky-200 bg-sky-50 shadow-sm text-sm font-medium text-sky-700 hover:bg-sky-100 transition"
            onClick={this.openSmartPaste}
          >
            <SparklesIcon className="h-4 w-4 text-sky-500" />
            AI Write / Paste
          </button>
        </div>

        {/* ── Priority columns ── */}
        <div className="grid grid-cols-4 gap-3 items-start">
          {PRIORITIES.map(p => this.renderPriorityColumn(p))}
        </div>

        {/* ── Detail panel ── */}
        {selected && this.renderDetailPanel(selected)}

        {/* ── Modals ── */}
        {this.renderSmartPasteModal()}
        {this.renderMergeModal()}


      </div>
    );
  }
}

export default CarePlan;
